import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/ui/tabs";
import {
  // getAnomalySensitivity,
  getThresholdValue,
  insertThresholdValue,
  // setCustomSensitivity,
} from "@services/api/amlAnomalyDetection";
import { Button } from "@components/ui/button";
import * as Slider from "@radix-ui/react-slider";
import { useMutation, useQuery } from "react-query";
import { useToast } from "@components/ui/use-toast";
import { Badge } from "@components/ui/badge";
import CustomSelect from "@components/common/CustomSelect";
import AnomalyDefeat from "../usecase-builder/AnomalyDefeat";

interface AnomalyTunningProps {
  show: boolean;
  onClose: () => void;
  eventList?: { label: string; value: string }[];
  selectedEvent?: string;
  eventId?: string;
  data?: any;
  anomalyName?: string;
  usecaseId?: number;
}

// type CustomSensitivityType = "low" | "medium" | "high";

// const customSensitivityMap: Record<CustomSensitivityType, number> = {
//   low: 0,
//   medium: 1,
//   high: 2,
// };

// const sensitivityLabels: CustomSensitivityType[] = ["low", "medium", "high"];

const percentages = Array.from({ length: 11 }, (_, i) => -100 + i * 20);

const AnomalyTuningModal: React.FC<AnomalyTunningProps> = ({
  show,
  onClose,
  eventId,
  data,
  // anomalyName,
  // usecaseId,
}) => {
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState("threshold");
  const [thresholdScore, setThresholdScore] = useState<number[]>([0]);
  const [baseValue, setBaseValue] = useState(0);
  const [thresholdScoreWithPercentage, setThresholdScoreWithPercentage] =
    useState(0);

  // const [selectedSensitivity, setSelectedSensitivity] =
  //   useState<CustomSensitivityType>("medium");

  const [selectedEvent, setSelectedEvent] = useState(
    data?.eventNames ? Object.keys(data.eventNames)[0] : "",
  );

  const { data: thresholdData } = useQuery(
    ["getThresholdValue", eventId],
    ({ signal }) => getThresholdValue({ eventId: [eventId] }, signal),
    {
      enabled: !!eventId,
    },
  );

  // const { data: sensitivityData } = useQuery(
  //   ["getAnomalySensitivity", usecaseId],
  //   ({ signal }) => getAnomalySensitivity(usecaseId as number, signal),
  //   {
  //     enabled: !!usecaseId,
  //   },
  // );

  const thresholdKey = useMemo(() => {
    if (
      !thresholdData ||
      typeof thresholdData !== "object" ||
      Object.keys(thresholdData).length === 0
    ) {
      return "";
    }

    return Object.keys(thresholdData)[0];
  }, [thresholdData]);

  const thresholdInfo = thresholdKey ? thresholdData?.[thresholdKey] : null;

  const thresholdDetails = thresholdInfo?.thresholdValues?.field_details;
  const DEFAULT_THRESHOLD_PERCENT =
    thresholdInfo?.anomalyThreshold?.[0]?.value || 20;

  const conditionValue = thresholdInfo?.conditionVal;

  const hasThresholdData =
    !!thresholdInfo &&
    !!thresholdDetails &&
    conditionValue !== undefined &&
    conditionValue !== null;

  // useEffect(() => {
  //   if (sensitivityData?.data?.sensitivity_score) {
  //     setSelectedSensitivity(sensitivityData.data.sensitivity_score);
  //   }
  // }, [sensitivityData]);

  useEffect(() => {
    if (
      conditionValue === undefined ||
      conditionValue === null ||
      isNaN(Number(conditionValue))
    ) {
      setBaseValue(0);
      setThresholdScore([DEFAULT_THRESHOLD_PERCENT]);
      setThresholdScoreWithPercentage(0);
      return;
    }

    const numValue = Number(conditionValue);
    const updated = (numValue * DEFAULT_THRESHOLD_PERCENT) / 100;

    setBaseValue(numValue);
    setThresholdScore([DEFAULT_THRESHOLD_PERCENT]);
    setThresholdScoreWithPercentage(numValue + updated);
  }, [conditionValue]);

  const eventOptions = useMemo(() => {
    if (!data?.eventNames) return [];

    return Object.entries(data.eventNames).map(([key, value]) => ({
      value: key,
      label: value as string,
    }));
  }, [data]);

  const canUseNegativeThreshold = baseValue > 2;

  const sliderMin = canUseNegativeThreshold ? -100 : 0;

  const sliderPercentages = canUseNegativeThreshold
    ? percentages
    : percentages.filter((mark) => mark >= 0);

  const handleThresholdScore = (value: number[]) => {
    const percent = value[0];

    const updated = (baseValue * percent) / 100;

    setThresholdScore(value);
    setThresholdScoreWithPercentage(baseValue + updated);
  };

  const insertThresholdMutation = useMutation(
    (payload: any) => insertThresholdValue(payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Threshold conditions inserted successfully",
          variant: "success",
        });
        setActiveTab("sensitivity");
      },
      onError: () => {
        toast({
          title: "Error",
          description: "Failed to insert threshold conditions",
          variant: "destructive",
        });
      },
    },
  );

  // const setCustomSensitivityMutation = useMutation(
  //   (payload: any) => setCustomSensitivity(payload),
  //   {
  //     onSuccess: () => {
  //       toast({
  //         title: "Success",
  //         description: "Custom sensitivity inserted successfully",
  //         variant: "success",
  //       });
  //       onClose();
  //     },
  //     onError: () => {
  //       toast({
  //         title: "Error",
  //         description: "Failed to insert custom sensitivity",
  //         variant: "destructive",
  //       });
  //     },
  //   },
  // );

  const buildThresholdConditionPayload = () => {
    const percentValue = thresholdScore?.[0] ?? 0;

    if (percentValue === 0) {
      toast({
        title: "Warning",
        description: "Please select a non-zero percentage value",
        variant: "warning",
      });

      return null;
    }

    const thresholdEventId = eventId || selectedEvent;

    if (!thresholdEventId) {
      return null;
    }

    return [
      {
        eventId: thresholdEventId,
        conditions: [
          {
            condition: percentValue > 0 ? "increase" : "decrease",
            value: Math.abs(percentValue),
          },
        ],
      },
    ];
  };

  const handleAddThreshold = () => {
    const payload = buildThresholdConditionPayload();

    if (!payload) return;

    insertThresholdMutation.mutate(payload);
  };

  // const handleSensitivityTuning = () => {
  //   if (!usecaseId) {
  //     toast({
  //       title: "Error",
  //       description: "Usecase ID is missing",
  //       variant: "destructive",
  //     });

  //     return;
  //   }

  //   const payload = {
  //     [usecaseId]: selectedSensitivity,
  //   };

  //   setCustomSensitivityMutation.mutate(payload);
  // };

  //hide Custom Sensitivity Tuning for now, as it is not required in the 4.7.7

  return (
    <Dialog open={show} onOpenChange={onClose}>
      <DialogContent
        className="max-w-[60vw]"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-white">Threshold Tuning</DialogTitle>

          {/* <DialogDescription>
            <span className="font-semibold">Anomaly Name</span> - {anomalyName}
          </DialogDescription> */}
        </DialogHeader>

        <div className="w-full text-gray-300">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
            {hasThresholdData && (
              <TabsList className="grid w-[300px] grid-cols-1">
                {/* <TabsTrigger value="sensitivity">Sensitivity Tuning</TabsTrigger> */}

                <TabsTrigger value="threshold">Threshold Tuning</TabsTrigger>
              </TabsList>
            )}

            {!hasThresholdData && (
              <p>No Threshold Added Against this Anomaly</p>
            )}

            {hasThresholdData && (
              <div className="w-[30%] m-5 ml-0">
                <CustomSelect
                  data={eventOptions}
                  defaultValue={selectedEvent}
                  placeholder="Select an Event"
                  onSelect={setSelectedEvent}
                />
              </div>
            )}

            {conditionValue && thresholdDetails && (
              <TabsContent
                value="threshold"
                className="p-4 text-sm text-gray-300"
              >
                <div className="flex items-center justify-between pr-20">
                  <div className="flex items-center gap-x-4 rounded-lg border border-border bg-muted/40 px-2 py-3 shadow-sm">
                    <p className="text-sm font-medium text-foreground">
                      Default value
                    </p>

                    <Badge>{baseValue}</Badge>
                  </div>

                  <div className="flex items-center gap-x-4 rounded-lg border border-border bg-muted/40 px-2 py-3 shadow-sm">
                    <p className="text-sm font-medium text-foreground">
                      New value
                    </p>

                    <Badge>{Math.round(thresholdScoreWithPercentage)}</Badge>
                  </div>
                </div>

                <div className="w-full mt-20">
                  <Slider.Root
                    value={thresholdScore}
                    onValueChange={handleThresholdScore}
                    defaultValue={[DEFAULT_THRESHOLD_PERCENT]}
                    min={sliderMin}
                    max={100}
                    step={20}
                    className="w-[80%] ms-[10%] h-[8px] relative flex mb-10"
                  >
                    <Slider.Track className="h-1 bg-foreground rounded-full w-full relative">
                      <Slider.Range className="h-full" />

                      {sliderPercentages.map((mark) => {
                        const position =
                          ((mark - sliderMin) / (100 - sliderMin)) * 100;

                        return (
                          <div key={mark}>
                            <div
                              className="absolute w-[1px] h-[10px] bg-white top-2"
                              style={{
                                left: `${position}%`,
                              }}
                            />

                            <div
                              className="absolute top-4 text-xs whitespace-nowrap -translate-x-1/2"
                              style={{
                                left: `${position}%`,
                              }}
                            >
                              {mark}
                            </div>
                          </div>
                        );
                      })}
                    </Slider.Track>

                    <Slider.Thumb className="bg-primary h-[20px] w-[20px] block border-white border-2 rounded-full -mt-[8px] shadow-sm">
                      <div className="bg-primary text-white text-xs -translate-y-14 left-1/2 -translate-x-1/2 absolute py-1 px-2 rounded-sm whitespace-nowrap">
                        {thresholdScore?.[0] ?? 0} %
                      </div>

                      <ChevronDown size={16} className="-mt-6 animate-bounce" />
                    </Slider.Thumb>
                  </Slider.Root>
                </div>

                <div className="pr-16 pt-4">
                  <Button
                    className="float-right"
                    onClick={handleAddThreshold}
                    disabled={insertThresholdMutation.isLoading}
                  >
                    {insertThresholdMutation.isLoading && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Submit
                  </Button>
                </div>
              </TabsContent>
            )}

            <TabsContent value="selectMeta">
              <AnomalyDefeat />
            </TabsContent>

            {/* <TabsContent
              value="sensitivity"
              className="p-4 text-sm text-gray-300"
            >
              <div className="space-y-3">
                <div className="w-full mt-6">
                  <div className="text-base font-bold mb-16">
                    Custom Sensitivity <sup>*</sup>
                  </div>

                  <Slider.Root
                    value={[customSensitivityMap[selectedSensitivity]]}
                    onValueChange={(value: number[]) =>
                      setSelectedSensitivity(sensitivityLabels[value[0]])
                    }
                    min={0}
                    max={2}
                    step={1}
                    className="w-[80%] ms-[10%] h-[8px] relative flex mb-6"
                  >
                    <Slider.Track className="h-1 bg-foreground rounded-full w-full">
                      <Slider.Range className="w-full h-full" />

                      <div className="absolute w-[1px] top-2 h-[10px] bg-white ms-[10px]" />
                      <div className="absolute top-2 text-xs mt-3 whitespace-nowrap -translate-x-[50%] ms-[10px]">
                        Low
                      </div>

                      <div className="absolute w-[1px] top-2 h-[10px] bg-white left-[50%]" />
                      <div className="absolute top-2 text-xs mt-3 whitespace-nowrap left-[50%] -translate-x-[50%]">
                        Medium
                      </div>

                      <div className="absolute w-[1px] top-2 h-[10px] bg-white left-[100%] -ms-[10px]" />
                      <div className="absolute top-2 text-xs mt-3 whitespace-nowrap left-[100%] -translate-x-[50%] -ms-[10px]">
                        High
                      </div>
                    </Slider.Track>

                    <Slider.Thumb className="bg-primary h-[20px] w-[20px] block border-white border-2 rounded-full -mt-[8px] shadow-sm overflow-visible">
                      <div className="bg-primary text-white text-xs -translate-y-14 ms-[10px] inline-block py-2 px-4 rounded-sm -translate-x-[50%] whitespace-nowrap">
                        {selectedSensitivity}
                      </div>

                      <ChevronDown
                        size={16}
                        className="-mt-12 animate-bounce"
                      />
                    </Slider.Thumb>
                  </Slider.Root>

                  <div className="float-right mt-5">
                    <Button
                      onClick={handleSensitivityTuning}
                      disabled={setCustomSensitivityMutation.isLoading}
                    >
                      {setCustomSensitivityMutation.isLoading && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Add Sensitivity
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent> */}
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AnomalyTuningModal;
